import React, { createContext } from 'react';

import './index.scss';
import { BrowserRouter } from 'react-router-dom';
import { StyledEngineProvider } from '@mui/material';
import ReactDOM from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import * as Sentry from '@sentry/react';
import { browserTracingIntegration, replayIntegration } from '@sentry/react';

import { ApplicationRoutes } from 'routes/ApplicationRoutes';
import { RootStore } from 'store/root.store';
import ErrorPage from 'components/errors/ErrorPage/ErrorPage';
import ToastContainer from 'components/common/Toast/ToastContainer';
import { ENVIRONMENT } from 'constants/constants';
import { IGNORED_SENTRY_ERRORS, SENSITIVE_HEADERS } from 'constants/sentry.constants';
import { ApplicationEnvironments } from 'common/enums/applicationEnvironments.enum';

import reportWebVitals from './reportWebVitals';

const initSentry = () => {
  if (![ApplicationEnvironments.Production, ApplicationEnvironments.Testing].includes(ENVIRONMENT)) {
    return;
  }

  Sentry.init({
    environment: ENVIRONMENT,
    dsn: process.env.REACT_APP_SENTRY_DSN,
    tracesSampleRate: 0.1,
    ignoreErrors: IGNORED_SENTRY_ERRORS,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Granular, PII-safe collection: no auto user/IP (a non-PII account id is set manually in
    // useSentryContext), no cookies, no request/response bodies (they carry federal tax id and
    // business owner details), and the auth header stripped.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpBodies: [],
      httpHeaders: {
        request: { deny: SENSITIVE_HEADERS },
        response: { deny: SENSITIVE_HEADERS },
      },
    },
    integrations: [
      browserTracingIntegration(),
      replayIntegration({
        blockAllMedia: true,
        maskAllInputs: true,
        maskAllText: true,
      }),
    ],
  });
};

initSentry();

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

export const RootStoreContext = createContext<RootStore>({} as RootStore);

root.render(
  <ErrorBoundary FallbackComponent={ErrorPage}>
    <BrowserRouter>
      <StyledEngineProvider injectFirst>
        <RootStoreContext.Provider value={new RootStore()}>
          <ApplicationRoutes />
          <ToastContainer />
        </RootStoreContext.Provider>
      </StyledEngineProvider>
    </BrowserRouter>
  </ErrorBoundary>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
